<template>
    <v-card elevation="0" class="px-4">

        <v-divider></v-divider>

        <div class="d-flex pt-3 pb-7 mt-2">
            <v-text-field style="flex: 0 1 300px" v-model="search" append-icon="mdi-magnify" outlined
                :label="$t('sales.clientsList.search')" dense hide-details />
            <v-spacer></v-spacer>

            <vue-json-to-csv :json-data="docToExport" :csv-title="`Ingressos y documentos (${computedDateFormatted})`"
                :separator="';'" :labels="csvLabels">
                <v-btn outlined color="primary" class="ml-2" height="40">
                    {{ $t('common.export') }}
                    <v-icon>mdi-download</v-icon>
                </v-btn>
            </vue-json-to-csv>
        </div>

        <!-- --------------------------- -->
        <!--   TABELA PRINCIPAL          -->
        <!-- --------------------------- -->

        <v-card outlined>
            <v-data-table :loading="loading" :headers="headers" :items="docs" :items-per-page="25" :search="search"
                :footer-props="{
                    showFirstLastPage: true,
                    'items-per-page-options': [10, 25, 50]
                }">

                <!-- createdOn -->
                <template v-slot:[`item.createdOn`]="{ item }">
                    <span class="hide-text">{{ item.createdOn }}</span>{{ item.createdOn | dateTime }}
                </template>

                <!-- paymentInfo -->
                <template v-slot:[`item.paymentInfo`]="{ item }">
                    {{ $t(`billing.payment.${item.paymentInfo.method}`) }}
                </template>

                 <template v-slot:[`item.status`]="{ item }">
                    <v-chip small :color="$t(`whiteLabelStatus.${item.status}[1]`)">{{ $t(`whiteLabelStatus.${item.status}[0]`) }}</v-chip>
                </template>

                <!-- gridId -->
                <template v-slot:[`item.gridId`]="{ item }">
                    {{ item.name }} ({{ item.gridId }})
                </template>

                <!-- invoices -->
                <template v-slot:[`item.invoices`]="{ item }">
                    <div class="d-flex align-center">

                        <!-- Mostra número de invoices -->


                        <v-btn small outlined color="primary" @click="openInvoiceManager(item)">
                            {{ $t('billing.manageInvoices') }}
                            <span v-if="item.invoices && item.invoices.length" class="px-1">
                                <v-badge :content="item.invoices.length" class="ml-2"
                                    color="green" overlap>
                                </v-badge>
                            </span>
                            <v-icon v-else small>mdi-file-document-edit</v-icon>
                        </v-btn>

                    </div>
                </template>

            </v-data-table>
        </v-card>

        <!-- --------------------------- -->
        <!--    MODAL: GERENCIAR NF      -->
        <!-- --------------------------- -->

        <v-dialog v-model="file.show" width="540">
            <v-card v-if="file.item">

                <v-card-title class="pb-4">
                    {{ $t('billing.manageInvoices') }}
                </v-card-title>

                <v-card-subtitle class="pb-2">
                    <v-icon x-small>mdi-pound</v-icon>
                    {{ file.item.id }} —
                    <b>{{ file.item.name }}</b>
                </v-card-subtitle>

                <v-card-text>

                    <!-- ⚡ AQUI: apenas o módulo de upload -->
                    <FileUpload v-if="file.show" ref="fileUpload" :snap="file.item.invoices" type="nf"
                        :target="`campaign/invoices/${file.item.id}`" @set-upload="updateInvoices" />

                    <div class="d-flex pt-3 mt-3">
                        <v-spacer />

                        <v-btn text class="mx-1" @click="cancelManager">
                            {{ $t('common.cancel') }}
                        </v-btn>

                        <v-btn color="success" elevation="0" :disabled="isUploading" @click="saveInvoices">
                            {{ $t('common.save') }}
                        </v-btn>
                    </div>

                </v-card-text>

            </v-card>
        </v-dialog>

    </v-card>
</template>

<script>
import moment from 'moment'
import config from '@/store/config'
import { initializeApp } from 'firebase/app'
import { getFirestore, collection, query, where, getDocs, updateDoc, doc, getDoc } from "firebase/firestore"
import VueJsonToCsv from 'vue-json-to-csv'
import { mapActions, mapState } from 'vuex'
import FileUpload from '@/interface/FileUpload.vue'

export default {
    name: 'BillingTable',
    components: { FileUpload, VueJsonToCsv },

    data() {
        return {
            documents: [],
            accounts: [],
            currentPlan: null,
            loading: true,
            search: '',
            file: {
                show: false,
                item: null
            }
        }
    },

    async mounted() {
        await this.getCurrentPlan()
        this.loadDocuments()
    },
    watch: {
        'file.show': {
            handler(nv) {
                if (!nv)
                    this.file.item.invoices = []
            }
        }
    },

    computed: {
        ...mapState('groupStores', ['masterStore']),
        csvLabels() {
            return {
                document: { title: 'CNPJ' },
                company: { title: 'Empresa' },
                campaign: { title: 'ID Campanha' },
                price: { title: 'Total bruto faturado' },
                finalPrice: { title: "Total líquido recebido" },
                paymentType: { title: "Método de pagamento" },
                comprobatory: { title: "Comprobatórios" },
                invoice: { title: "Nota fiscal" }
            }
        },
        headers() {
            return [
                { text: this.$t('financial.billingList.doc'), value: 'document' },
                { text: this.$t('financial.billingList.company'), value: 'company' },
                { text: this.$t('financial.billingList.gridId'), value: 'gridId' },
                { text: this.$t('financial.billingList.grossOrder'), value: 'grossOrder' },
                { text: this.$t('financial.financialTable.netAmountReceivable'), value: 'netToReceive', align: 'center' },
                { text: this.$t('financial.billingList.dateAndMethod'), value: 'paymentInfo' },
                { text: this.$t('financial.billingList.status'), value: 'status'},
                { text: this.$t('billing.invoicesTitle'), value: 'invoices', sortable: false }
            ]
        },

        computedDateFormatted() {
            return moment().format('YYYY-MM-DD HH:mm')
        },

        docToExport() {
            return this.docs.map(doc => ({
                document: doc.document,
                company: doc.company,
                campaign: doc.gridId,
                price: doc.grossOrder,
                finalPrice: doc.netToReceive,
                paymentType:
                    doc.paymentInfo.method === 'credit_card'
                        ? 'Cartão de crédito'
                        : doc.paymentInfo.method === 'pix'
                            ? 'PIX'
                            : 'Boleto',
                comprobatory: doc.files.length,
                invoice: doc.invoices.length
            }))
        },

        docs() {
            return this.documents.map(doc => {
                const acc = this.accounts[doc.user.uid]
                const companyDocument = acc && acc.document ? acc.document : ''
                const companyName = acc && acc.company ? acc.company : ''
                return {
                    id: doc.id,
                    gridId: doc.id,
                    name: doc.name,
                    paymentInfo: doc.paymentInfo,
                    document: companyDocument,
                    company: companyName,
                    status: doc.status,
                    files: doc.files || [],
                    invoices: doc.invoices || [],
                    grossOrder: this.$n(doc.total.final, 'currency'),
                    netToReceive: `${this.$n(
                        (1 - this.masterStore.piInfo.commRatio) * doc.total.final,
                        'currency'
                    )} (${this.$n((1 - this.masterStore.piInfo.commRatio) * 100)}%)`
                }
            })
        },

        isUploading() {
            const up = this.$refs.fileUpload
            return up && up.loadingProcessing
        }
    },

    methods: {
        ...mapActions('groupStores', ['getMasterStore']),

        async openInvoiceManager(item) {
            const firebase = initializeApp(config);
            const db = getFirestore(firebase);

            const orderRef = doc(db, "orders", item.id);
            const snapshot = await getDoc(orderRef);

            if (snapshot.exists()) {
                // Sempre usa os dados mais recentes do Firestore
                this.file.item = snapshot.data();
            } else {
                // fallback (nunca deve ocorrer)
                this.file.item = JSON.parse(JSON.stringify(item));
            }

            this.file.show = true;
        },

        cancelManager() {
            this.file.show = false
        },

        updateInvoices(updatedList) {
            this.file.item.invoices = updatedList
        },

        async saveInvoices() {
            const app = initializeApp(config)
            const db = getFirestore(app)
            const ref = doc(db, 'orders', this.file.item.id)

            // Remove File (objeto nativo do browser) e data (base64) — não serializáveis pelo Firestore
            const invoices = (this.file.item.invoices || []).map(({ File: _f, data: _d, ...rest }) => rest)

            await updateDoc(ref, { invoices })

            this.cancelManager()
            this.loadDocuments()
        },

        async getCurrentPlan() {
            const url = `whitelabel/signature`
            try {
                const resp = await this.$axios.get(url)
                this.currentPlan = resp.data.data
            } catch (err) {
                console.error(err)
            }
        },

        loadDocuments() {
            const app = initializeApp(config)
            const db = getFirestore(app)

            const q = query(
                collection(db, 'orders'),
                where('status', 'in', [
                    'paid',
                    'waiting_content',
                    'awaiting_content_approval',
                    'content_reproved',
                    'content_approved',
                    'waiting_pi_approval',
                    'done',
                    'waiting_document',
                    'finish_placement',
                    'documents_sent'
                ])
            )

            getDocs(q).then(snapshot => {
                const list = []
                snapshot.forEach(doc => list.push(doc.data()))
                this.documents = list
                this.loadAccounts()
            })
        },

        loadAccounts() {
            const app = initializeApp(config)
            const db = getFirestore(app)

            getDocs(collection(db, 'accounts')).then(snapshot => {
                const accs = {}
                snapshot.forEach(doc => {
                    accs[doc.data().uid] = doc.data()
                })
                this.accounts = accs
                this.loading = false
            })
        }
    }
}
</script>

<style scoped>
.hide-text {
    font-size: 0;
    text-indent: -999em;
}
</style>
